import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, TextArea } from 'tamagui'
import { CobuAlert, CobuBackButton, CobuHeader, CobuStack, CobuText, CobuTextAvatar } from '@cobu/ui'
import { useComposePlaceComment } from '../../hooks/useComposePlaceComment'
import { Strings } from '../../constants/Strings'
import { getModerationAlertConfig } from '../../constants/moderation'
import FlaggedTermsPreview from '../moderation/FlaggedTermsPreview'

/**
 * Compose screen for a place's neighbor-chat comment — mirrors the layout of
 * the feed's ComposeComment screen (header with back + Post, avatar + textarea),
 * including its moderation alert and flagged-term highlighting.
 * Flat comments only; no reply target.
 */
export default function ComposePlaceComment() {
  const navigate = useNavigate()
  const {
    user,
    contents,
    setContents,
    isSubmitting,
    handlePost,
    flaggedTerms,
    moderationAlert,
    closeModerationAlert,
    handleModerationDecision,
  } = useComposePlaceComment()
  const strings = Strings.get().places.compose

  const moderationConfig = moderationAlert.type
    ? getModerationAlertConfig(moderationAlert.type)
    : null

  return (
    <CobuStack f={1} bg="$white">
      <CobuHeader
        title={strings.title}
        renderLeft={() => <CobuBackButton onPress={() => navigate(-1)} />}
        renderRight={() => (
          <Button
            unstyled
            onPress={handlePost}
            disabled={isSubmitting || !contents.trim()}
            bg="transparent"
            bw={0}
          >
            <CobuText color="$textLinkColor" fontSize="$4" opacity={isSubmitting || !contents.trim() ? 0.5 : 1}>
              {strings.post}
            </CobuText>
          </Button>
        )}
      />

      <CobuStack layout="horizontal" gap="$3" ai="flex-start" px="$4" py="$4" f={1}>
        <CobuTextAvatar name={user?.displayName || user?.firstName || 'You'} uuid={user?.uuid || ''} size="$8" />
        <CobuStack f={1}>
          <TextArea
            value={contents}
            onChangeText={setContents}
            placeholder={strings.placeholder}
            col="$color"
            fos="$3"
            p="$3"
            mih="$10"
            bg="transparent"
            borderWidth={0}
            multiline
            numberOfLines={10}
            autoFocus
          />
          <FlaggedTermsPreview contents={contents} flaggedTerms={flaggedTerms} />
        </CobuStack>
      </CobuStack>

      {moderationAlert.open && moderationConfig && (
        <CobuAlert
          open={moderationAlert.open}
          onOpenChange={(isOpen) => !isOpen && closeModerationAlert()}
          title={moderationConfig.title}
          description={moderationConfig.message}
          actions={moderationConfig.options.map((option) => ({
            text: option.text,
            onPress: () => handleModerationDecision(option.text),
            style: option.style,
          }))}
        />
      )}
    </CobuStack>
  )
}
